import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import {
  ADMIN_API_BASE_URL,
  API_ADMIN_ROUTES,
  API_BASE_URL,
} from "./constants";
import useUserStore from "./store/User.store";
import checkSession from "./utils/cognito/checkSession";

export const isFullURL = (url) => /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
export const isAdminRoute = (url) => {
  const adminRoutes = Object.values(API_ADMIN_ROUTES);
  return adminRoutes.includes(url);
};
export const getFullAPIUrl = (url) => {
  if (isFullURL(url)) return url;
  if (isAdminRoute(url)) return `${ADMIN_API_BASE_URL}/${url}`;
  return `${API_BASE_URL}${url}`;
};

const redirectUnauthorized = () => {
  localStorage.removeItem("remi-user");
  window.location = "/signin";
  return null;
};

const redirect404 = () => {
  window.location = "/404";
  return null;
};

const useToken = () => {
  const user = useUserStore((state) => state.data.user);
  return user?.accessToken;
};

export const useFetchData = ({ url }, shouldFetch = true) => {
  const token = useToken();
  return useSWR(shouldFetch ? { url, token } : null, fetcher);
};

export const useFetchDataImmutable = ({ url }, shouldFetch = true) => {
  const token = useToken();
  return useSWRImmutable(shouldFetch ? { url, token } : null, fetcher);
};

export const useFetchMetadata = (
  { url, method, data, ignoreRedirectsOnError },
  shouldFetch = true
) => {
  const token = useToken();
  return useSWRImmutable(
    shouldFetch ? { url, token, method, data, ignoreRedirectsOnError } : null,
    fetcher
  );
};

export const fetcher = async ({
  url,
  token,
  method = "GET",
  data = {},
  ignoreRedirectsOnError = false,
  reportId = "",
}) => {
  if (!url) {
    return null;
  }

  if (!token) {
    return redirectUnauthorized();
  }

  const sessionResponse = await checkSession();
  if (sessionResponse.code !== 200) {
    return redirectUnauthorized();
  }

  const fullURL = getFullAPIUrl(url);

  const args = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (method === "POST") {
    args["headers"]["Content-Type"] = "application/json";
    args["body"] = JSON.stringify(data);
  }
  const res = await fetch(fullURL, args);

  if (!res.ok) {
    if (ignoreRedirectsOnError) {
      return null;
    } else if (res?.status === 401) {
      redirectUnauthorized();
    } else {
      redirect404();
    }
  }

  const contentType = res.headers.get("content-type");
  if (contentType === "application/pdf") {
    res.blob().then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Report-${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blob);
    });
    return null;
  }

  return res.json();
};
